/**
 * The success page's fallback when checkout verification can't confirm the upgrade yet:
 * re-read the signed-in user until their plan turns paid, for a bounded time.
 *
 * Read-only by design — it only ever calls the `fetchUser` it's given (GET /auth/user).
 * It never starts a checkout, verifies a session, or asks the server to reconcile; the
 * webhook (or a later verify) is what actually grants the plan.
 *
 * Pure (no imports, clock and sleep injectable) so it runs under `node --test`.
 */

export const POLL_INTERVAL_MS = 2_000;
export const POLL_TIMEOUT_MS = 15_000;

/** Stored plan values that mean "the paid upgrade has landed". */
export function isPaidPlanValue(plan: string | null | undefined): boolean {
  return plan === "plus" || plan === "premium";
}

export type PollOutcome<U> = { kind: "paid"; user: U } | { kind: "timeout" } | { kind: "aborted" };

export interface PollOptions<U extends { plan?: string | null }> {
  fetchUser: () => Promise<U | null>;
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  now?: () => number;
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
}

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms);
    signal?.addEventListener("abort", done, { once: true });
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    }
  });
}

/**
 * Checks immediately, then every `intervalMs`, stopping at the first paid plan or once
 * `timeoutMs` has passed. A failed read (network blip, 5xx) just counts as "not yet".
 * The attempt count is also capped, so it ends even if the clock misbehaves.
 */
export async function pollUntilPaid<U extends { plan?: string | null }>(opts: PollOptions<U>): Promise<PollOutcome<U>> {
  const intervalMs = opts.intervalMs ?? POLL_INTERVAL_MS;
  const timeoutMs = opts.timeoutMs ?? POLL_TIMEOUT_MS;
  const now = opts.now ?? Date.now;
  const sleep = opts.sleep ?? defaultSleep;
  const maxAttempts = Math.floor(timeoutMs / intervalMs) + 1;
  const deadline = now() + timeoutMs;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (opts.signal?.aborted) return { kind: "aborted" };
    try {
      const user = await opts.fetchUser();
      if (opts.signal?.aborted) return { kind: "aborted" };
      if (user && isPaidPlanValue(user.plan)) return { kind: "paid", user };
    } catch {
      // Not yet — keep going until the deadline.
    }
    if (attempt === maxAttempts || now() + intervalMs > deadline) break;
    await sleep(intervalMs, opts.signal);
  }
  return opts.signal?.aborted ? { kind: "aborted" } : { kind: "timeout" };
}

/**
 * How the success page should treat a failed verify call:
 * - `incomplete`: Stripe hasn't marked the session complete yet → poll.
 * - `temporary`: network failure, rate limit or server error → poll.
 * - `permanent`: the session isn't this user's / isn't valid, or they're signed out →
 *   show the error; polling can't fix it.
 */
export function verifyFailureKind(err: unknown): "incomplete" | "temporary" | "permanent" {
  const { status, code } = (err ?? {}) as { status?: unknown; code?: unknown };
  if (code === "checkout_incomplete") return "incomplete";
  if (typeof status !== "number") return "temporary"; // fetch threw: offline, DNS, CORS…
  if (status === 408 || status === 429 || status >= 500) return "temporary";
  return "permanent";
}
